// src/app/providers/router.ts
import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/entities/auth'
import { routes } from '@/pages'
import { env } from '@/shared/config'
import { getAuthTokens, isAuthenticated, setAuthTokens } from '@/shared/lib'

/**
 * Attempt to refresh access token using refresh token
 * Returns new access token or null if refresh fails
 */
async function tryRefreshToken(): Promise<string | null> {
  const { refreshToken } = getAuthTokens()
  if (!refreshToken) 
    return null

  try {
    const resp = await fetch(`${env.VITE_API_BASE_URL}/token/refresh/`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh: refreshToken }),
    })

    if (!resp.ok) 
      return null

    const data = await resp.json()
    const newAccess: string | undefined = data.access
    const newRefresh: string = data.refresh ?? refreshToken

    if (!newAccess) 
      return null

    setAuthTokens(newAccess, newRefresh)
    return newAccess
  }
  catch {
    return null
  }
}

const router = createRouter({
  history: createWebHistory(env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { top: 0 }
  },
})

// Implement authentication logic
router.beforeEach(async (to, from, next) => {
  let { accessToken } = getAuthTokens()
  const { refreshToken } = getAuthTokens()
  const requiresAuth = to.meta.requiresAuth !== false

  const authStore = useAuthStore()

  // Track if profile fetch failed due to server error (5xx)
  let serverError = false

  // If no access token but refresh token exists, try to refresh first
  // This handles the case when laptop wakes from sleep and access token expired
  if (!accessToken && refreshToken) {
    const newAccessToken = await tryRefreshToken()
    if (newAccessToken) {
      accessToken = newAccessToken
    }
  }

  // Always try to resolve profile first if user is authenticated and profile not loaded yet
  // Also refetch profile if we just refreshed the token (profile might be stale)
  if (isAuthenticated() && !authStore.profile) {
    try {
      await authStore.fetchProfile()
    }
    catch (error) {
      console.error('Failed to fetch profile:', error)
      // Check if it's a server error (5xx) - don't redirect in this case
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as { status: number }).status
        if (status >= 500 && status < 600) {
          serverError = true
        }
      }
    }
  }

  // Public routes: always allow
  if (!requiresAuth) {
    next()
    return
  }

  // Private routes: allow only when token exists AND profile has been fetched
  if (accessToken && authStore.profile) {
    next()
    return
  }

  // If token exists and profile fetch failed due to server error, still allow access
  // User should not be redirected to landing page due to backend issues
  if (accessToken && serverError) {
    console.warn(
      'Profile fetch failed due to server error, but allowing access with valid token',
    )
    next()
    return
  }

  // Otherwise, redirect to Home
  next({ name: 'Home' })
})

// Clear chunk reload flag on successful navigation
router.afterEach(() => {
  sessionStorage.removeItem(CHUNK_RELOAD_KEY)
})

// Handle chunk load failures after deployment (stale JS chunks)
// Uses sessionStorage flag to prevent infinite reload loops
const CHUNK_RELOAD_KEY = 'chunk_reload_attempted'

router.onError((error, to) => {
  const isChunkError
    = error.message.includes('Failed to fetch dynamically imported module')
    || error.message.includes('Importing a module script failed')
    || error.message.includes('Loading chunk')
    || error.message.includes('Unable to preload CSS')

  if (!isChunkError) 
    return

  const alreadyRetried = sessionStorage.getItem(CHUNK_RELOAD_KEY)
  if (alreadyRetried) {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY)
    return
  }

  sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
  window.location.href = to.fullPath
})

export { router }
